/**
 * 미션 제출 페이지 — 기존 제출 내역
 *
 * 현재 차수의 제출 내역을 카드 형태로 표시한다.
 * submitted 상태의 카드에서는 인라인 수정이 가능하다.
 */

import { useState } from 'react'
import type { MyApplicationResponse, MissionSubmissionResponse } from '@/lib/seedingPublicApi'
import { seedingPublicApi } from '@/lib/seedingPublicApi'
import { formatDateTime } from '@/features/apply/components/utils'
import type { UploadingFile } from './SubmissionForm'

interface SubmissionHistoryProps {
  application: MyApplicationResponse
  campaignId: string
  submissions: MissionSubmissionResponse[]
  effectiveSubmissionType: string
  previewMode: boolean
  /** 마감일 문자열 (기한 후 제출 표시용) */
  deadlineStr: string | null
  // 수정 모드 상태 (부모에서 관리)
  editingSubmissionId: string | null
  isEditFormOpen: boolean
  mediaUrls: string[]
  seedingUrl: string
  uploadingFiles: UploadingFile[]
  originalMediaUrls: string[]
  originalSeedingUrl: string
  mediaChanged: boolean
  linkChanged: boolean
  // 상태 세터
  setEditingSubmissionId: (id: string | null) => void
  setIsEditFormOpen: (open: boolean) => void
  setMediaUrls: (urls: string[] | ((prev: string[]) => string[])) => void
  setSeedingUrl: (url: string) => void
  setUploadingFiles: (files: UploadingFile[] | ((prev: UploadingFile[]) => UploadingFile[])) => void
  setOriginalMediaUrls: (urls: string[]) => void
  setOriginalSeedingUrl: (url: string) => void
  // 미디어 뷰어
  onViewMedia: (urls: string[], index: number) => void
  // 데이터 갱신
  onApplicationUpdate: (app: MyApplicationResponse) => void
}

export function SubmissionHistory({
  application,
  campaignId,
  submissions,
  effectiveSubmissionType,
  previewMode,
  deadlineStr,
  editingSubmissionId,
  isEditFormOpen,
  mediaUrls,
  seedingUrl,
  uploadingFiles,
  originalMediaUrls,
  originalSeedingUrl,
  mediaChanged,
  linkChanged,
  setEditingSubmissionId,
  setIsEditFormOpen,
  setMediaUrls,
  setSeedingUrl,
  setUploadingFiles,
  setOriginalMediaUrls,
  setOriginalSeedingUrl,
  onViewMedia,
  onApplicationUpdate,
}: SubmissionHistoryProps) {
  // 미디어/링크 저장 상태 (인라인 수정용)
  const [isSubmittingMedia, setIsSubmittingMedia] = useState(false)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [mediaSuccess, setMediaSuccess] = useState(false)
  const [isSubmittingLink, setIsSubmittingLink] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [linkSuccess, setLinkSuccess] = useState(false)

  if (submissions.length === 0) return null

  // 인라인 파일 선택 핸들러
  const handleFileSelect = async (files: FileList | null) => {
    if (!files || !application || previewMode) return

    const currentUploadingCount = uploadingFiles.length
    const newFiles = Array.from(files).map((file) => ({
      file,
      progress: 0,
      url: undefined as string | undefined,
      error: undefined as string | undefined,
    }))

    setUploadingFiles((prev) => [...prev, ...newFiles])

    for (let i = 0; i < newFiles.length; i++) {
      const fileEntry = newFiles[i]
      const fileIndex = currentUploadingCount + i

      try {
        const url = await seedingPublicApi.uploadFile(
          application.id,
          fileEntry.file,
          (progress) => {
            setUploadingFiles((prev) =>
              prev.map((f, idx) => idx === fileIndex ? { ...f, progress } : f),
            )
          },
        )
        setUploadingFiles((prev) =>
          prev.map((f, idx) => idx === fileIndex ? { ...f, url, progress: 100 } : f),
        )
        setMediaUrls((prev) => [...prev, url])
      } catch (err: any) {
        setUploadingFiles((prev) =>
          prev.map((f, idx) => idx === fileIndex ? { ...f, error: err.message || '업로드 실패' } : f),
        )
      }
    }
  }

  // 파일 제거
  const handleRemoveFile = (index: number) => {
    const removedUrl = uploadingFiles[index].url
    setUploadingFiles((prev) => prev.filter((_, i) => i !== index))
    if (removedUrl) {
      setMediaUrls((prev) => prev.filter((url) => url !== removedUrl))
    }
  }

  // 수정 모드: 미디어 저장 (PUT)
  const handleSaveMedia = async () => {
    if (!application || !editingSubmissionId) return
    if (mediaUrls.length === 0) {
      setMediaError('이미지 또는 영상을 하나 이상 업로드해주세요.')
      return
    }
    const stillUploading = uploadingFiles.some((f) => !f.url && !f.error)
    if (stillUploading) {
      setMediaError('파일 업로드가 완료될 때까지 기다려주세요.')
      return
    }

    setIsSubmittingMedia(true)
    setMediaError(null)
    setMediaSuccess(false)

    try {
      await seedingPublicApi.editSubmissionByPhone(
        application.id,
        editingSubmissionId,
        { media_urls: mediaUrls },
      )
      const appData = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      onApplicationUpdate(appData)
      setMediaSuccess(true)
      setTimeout(() => setMediaSuccess(false), 3000)
    } catch (err: any) {
      setMediaError(err.message || '저장에 실패했어요.')
    } finally {
      setIsSubmittingMedia(false)
    }
  }

  // 수정 모드: 링크 저장 (PUT)
  const handleSaveLink = async () => {
    if (!application || !editingSubmissionId) return
    if (!seedingUrl?.trim()) {
      setLinkError('링크를 입력해주세요.')
      return
    }

    setIsSubmittingLink(true)
    setLinkError(null)
    setLinkSuccess(false)

    try {
      await seedingPublicApi.editSubmissionByPhone(
        application.id,
        editingSubmissionId,
        { mission_url: seedingUrl.trim() },
      )
      const appData = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      onApplicationUpdate(appData)
      setLinkSuccess(true)
      setTimeout(() => setLinkSuccess(false), 3000)
    } catch (err: any) {
      setLinkError(err.message || '저장에 실패했어요.')
    } finally {
      setIsSubmittingLink(false)
    }
  }

  // 수정 모드 열기
  const openEditForm = (sub: MissionSubmissionResponse) => {
    setEditingSubmissionId(sub.id)
    setIsEditFormOpen(true)
    const urls = sub.media_urls || []
    const link = sub.mission_url || ''
    setMediaUrls(urls)
    setSeedingUrl(link)
    setOriginalMediaUrls(urls)
    setOriginalSeedingUrl(link)
    setUploadingFiles(
      urls.map((url) => ({
        file: new File([], url.split('/').pop() || 'file'),
        progress: 100,
        url,
      })),
    )
  }

  // 수정 모드 취소
  const cancelEdit = () => {
    setIsEditFormOpen(false)
    setMediaUrls(originalMediaUrls)
    setSeedingUrl(originalSeedingUrl)
    setUploadingFiles(
      originalMediaUrls.map((url) => ({
        file: new File([], url.split('/').pop() || 'file'),
        progress: 100,
        url,
      })),
    )
    setMediaError(null)
    setLinkError(null)
  }

  return (
    <div className="py-6 border-b border-border">
      <h3 className="text-base font-semibold text-on-surface mb-4">제출 내역</h3>

      <div className="space-y-4">
        {submissions.map((sub) => {
          const isEditing = sub.id === editingSubmissionId && isEditFormOpen

          return (
            <div key={sub.id} className={`border rounded-lg p-4 ${isEditing ? 'border-primary/50 bg-primary/5' : 'border-border'}`}>
              {/* 제출 일시 */}
              <p className="text-sm text-on-surface-variant">
                {formatDateTime(sub.created_at)} 제출
                {deadlineStr && new Date(sub.created_at) > new Date(deadlineStr) && (
                  <span className="ml-1.5 text-error font-medium">(기한 후 제출)</span>
                )}
              </p>
              {sub.status === 'rejected' && sub.updated_at && (
                <p className="text-sm text-on-surface-variant mt-0.5">{formatDateTime(sub.updated_at)} 반려</p>
              )}
              {sub.status === 'approved' && sub.updated_at && (
                <p className="text-sm text-on-surface-variant mt-0.5">{formatDateTime(sub.updated_at)} 승인</p>
              )}

              {/* 반려 사유 */}
              {sub.status === 'rejected' && sub.rejection_reason && (
                <div className="mt-2 mb-3 py-2 px-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  반려 사유: {sub.rejection_reason}
                </div>
              )}

              {isEditing ? (
                /* ===== 인라인 수정 폼 ===== */
                <div className="mt-3 space-y-3">
                  {/* 미디어 수정 */}
                  {effectiveSubmissionType !== 'link' && (
                    <>
                      {uploadingFiles.length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                          {uploadingFiles.map((file, index) => (
                            <div key={index} className="relative aspect-square bg-surface-low rounded-lg overflow-hidden">
                              {file.url ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const allUrls = uploadingFiles.filter((f) => f.url).map((f) => f.url!)
                                      const viewIndex = allUrls.indexOf(file.url!)
                                      onViewMedia(allUrls, viewIndex >= 0 ? viewIndex : 0)
                                    }}
                                    className="w-full h-full"
                                  >
                                    {file.file.type.startsWith('video/') || file.url?.match(/\.(mp4|mov|webm)$/i) ? (
                                      <video src={file.url} preload="metadata" muted className="w-full h-full object-cover" />
                                    ) : (
                                      <img src={file.url} alt="" className="w-full h-full object-cover" />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveFile(index)}
                                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                                  >
                                    ×
                                  </button>
                                </>
                              ) : file.error ? (
                                <div className="w-full h-full flex items-center justify-center bg-error/10 text-error text-xs p-2 text-center">{file.error}</div>
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center">
                                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mb-1"></div>
                                  <span className="text-xs text-on-surface-variant">{file.progress}%</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div
                        className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors"
                        onClick={() => document.getElementById('file-input-inline')?.click()}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5') }}
                        onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5') }}
                        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); handleFileSelect(e.dataTransfer.files) }}
                      >
                        <input
                          id="file-input-inline"
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          onChange={(e) => handleFileSelect(e.target.files)}
                          className="hidden"
                        />
                        <p className="text-sm text-on-surface-variant">클릭 또는 드래그하여 업로드</p>
                        <p className="text-xs text-outline-variant mt-0.5">이미지, 영상 (여러 개 선택 가능)</p>
                      </div>

                      {mediaError && (
                        <div className="p-2 bg-error/10 border border-error/30 rounded text-error text-xs">{mediaError}</div>
                      )}
                      {mediaSuccess && (
                        <div className="p-2 bg-green-50 border border-green-200 rounded text-green-600 text-xs">저장되었어요.</div>
                      )}

                      <button
                        onClick={handleSaveMedia}
                        disabled={isSubmittingMedia || uploadingFiles.some((f) => !f.url && !f.error) || mediaUrls.length === 0 || !mediaChanged}
                        className="w-full py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors disabled:opacity-50"
                      >
                        {isSubmittingMedia ? '저장 중...' : '저장하기'}
                      </button>
                    </>
                  )}

                  {/* 링크 수정 */}
                  {effectiveSubmissionType !== 'media' && (
                    <>
                      {effectiveSubmissionType === 'both' && (
                        <div className="border-t border-border pt-3" />
                      )}
                      <div>
                        <input
                          type="url"
                          value={seedingUrl}
                          onChange={(e) => setSeedingUrl(e.target.value)}
                          placeholder="https://..."
                          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                        />
                        <p className="text-xs text-outline-variant mt-1">SNS/블로그에 올린 미션 URL</p>
                      </div>

                      {linkError && (
                        <div className="p-2 bg-error/10 border border-error/30 rounded text-error text-xs">{linkError}</div>
                      )}
                      {linkSuccess && (
                        <div className="p-2 bg-green-50 border border-green-200 rounded text-green-600 text-xs">저장되었어요.</div>
                      )}

                      <button
                        onClick={handleSaveLink}
                        disabled={isSubmittingLink || !seedingUrl.trim() || !linkChanged}
                        className="w-full py-2 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors disabled:opacity-50"
                      >
                        {isSubmittingLink ? '저장 중...' : '저장하기'}
                      </button>
                    </>
                  )}

                  {/* 취소 버튼 */}
                  <button onClick={cancelEdit} className="w-full py-2 text-sm text-on-surface-variant hover:text-on-surface">
                    취소
                  </button>
                </div>
              ) : (
                /* ===== 읽기 전용 콘텐츠 ===== */
                <>
                  {sub.media_urls && sub.media_urls.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-2 mb-2">
                      {sub.media_urls.map((url, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => onViewMedia(sub.media_urls!, i)}
                          className="aspect-square bg-surface-low rounded-lg overflow-hidden hover:ring-2 hover:ring-primary transition-all"
                        >
                          {url.match(/\.(mp4|mov|webm)$/i) ? (
                            <video src={url} preload="metadata" muted className="w-full h-full object-cover" />
                          ) : (
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {sub.mission_url && (
                    <a
                      href={sub.mission_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-2 text-sm text-primary hover:underline truncate"
                    >
                      {sub.mission_url}
                    </a>
                  )}

                  {sub.status === 'submitted' && (
                    <button
                      onClick={() => openEditForm(sub)}
                      className="mt-3 w-full py-2 text-sm text-primary border border-primary/30 rounded-lg hover:bg-primary/10"
                    >
                      수정하기
                    </button>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
